import React, { useState } from 'react';
import { parseISO } from 'date-fns';
import cancelBookingDialog from './cancelBookingDialog';
import ConfirmDialog from './ConfirmDialog';
import DeskLegend from './DeskLegend';
import WeekGrid from './WeekGrid';
import { formatDate } from '../../../helpers/dates';
import { buildWeeks, dayKey, getDayState } from '../../../helpers/desk-booking';
import type { ConfirmDialogOptions } from './ConfirmDialog';
import type { WeekGridCell } from './WeekGrid';
import type { BookingIndex } from '../../../helpers/desk-booking';
import type {
  Desk, DeskBlock, DeskBooking, DeskBookingRange,
} from '../../../types';

type Props = {
  desk: Desk,
  index: BookingIndex,
  range: DeskBookingRange,
  holidays: Array<string>,
  today: string,
  targetUserId: number,
  targetUserName: string,
  actingForSelf: boolean,
  canManageOthers: boolean,
  busy: boolean,
  onBook: (date: string) => void,
  onCancel: (booking: DeskBooking) => void,
  onMove: (booking: DeskBooking) => void,
  onBlock: (date: string, reason: string) => void,
  onUnblock: (block: DeskBlock) => void,
  onSetActive: (active: boolean) => void,
  onClose: () => void,
};

// When is this desk free: Free / Taken (name) / You / Blocked / Holiday, nothing about other desks.
// Admins get a block mode for reserving the desk on given days (e.g. workshops).
const DeskCalendar = (props: Props): React.ReactElement => {
  const {
    desk, index, range, holidays, today, targetUserId, targetUserName, actingForSelf,
    canManageOthers, busy, onBook, onCancel, onMove, onBlock, onUnblock, onSetActive, onClose,
  } = props;
  const [blockMode, setBlockMode] = useState(false);
  const [dialog, setDialog] = useState<ConfirmDialogOptions | null>(null);
  const shortDate = (date: string) => formatDate(parseISO(date), 'EEE dd.MM.');

  const handleFree = (date: string) => {
    const existing = index.byUserDate[dayKey(targetUserId, date)];
    if (!existing) {
      onBook(date);

      return;
    }

    const from = index.desksById[existing.deskId]?.label || '?';
    setDialog({
      title: actingForSelf ? 'Move your booking?' : `Move ${targetUserName}'s booking?`,
      message: (
        <>
          {actingForSelf ? 'You already sit' : `${targetUserName} already sits`} at <strong>{from}</strong> on{' '}
          <strong>{shortDate(date)}</strong>. Move to <strong>{desk.label}</strong> instead?
        </>
      ),
      confirmLabel: `Move to ${desk.label}`,
      cancelLabel: `Keep ${from}`,
      onConfirm: () => onMove(existing),
    });
  };

  const handleBlock = (date: string) => {
    const booking = index.byDeskDate[dayKey(desk.id, date)];
    setDialog({
      title: `Block ${desk.label} on ${shortDate(date)}?`,
      message: booking
        ? <><strong>{booking.userName}</strong> has this desk booked that day. Their booking will be cancelled.</>
        : 'Nobody will be able to book this desk that day.',
      confirmLabel: 'Block desk',
      danger: !!booking,
      input: { label: 'Reason', defaultValue: 'Workshop', maxLength: 100 },
      onConfirm: (reason) => onBlock(date, reason),
    });
  };

  const handleToggleActive = () => {
    if (!desk.active) {
      onSetActive(true);

      return;
    }

    const upcoming = Object.values(index.byDeskDate).filter((booking) => booking.deskId === desk.id).length;
    setDialog({
      title: `Make ${desk.label} unbookable?`,
      message: upcoming
        ? <>Its <strong>{upcoming} upcoming booking{upcoming === 1 ? '' : 's'}</strong> will be cancelled.</>
        : 'Nobody will be able to book it until you make it bookable again.',
      confirmLabel: 'Make unbookable',
      danger: upcoming > 0,
      onConfirm: () => onSetActive(false),
    });
  };

  const renderBlockModeCell = (date: string): WeekGridCell => {
    const state = getDayState(desk.id, date, index, targetUserId, range, holidays);
    const block = index.blocksByDeskDate[dayKey(desk.id, date)];

    if (state === 'blocked' && block) {
      return {
        state,
        label: block.reason,
        disabled: false,
        onClick: () => setDialog({
          title: `Unblock ${desk.label} on ${shortDate(date)}?`,
          message: <>It is blocked for <strong>{block.reason}</strong>. After unblocking, anyone can book it.</>,
          confirmLabel: 'Unblock',
          onConfirm: () => onUnblock(block),
        }),
      };
    }
    if (state === 'holiday') return { state, label: 'Holiday', disabled: true };
    if (state === 'disabled') return { state, label: '', disabled: true };

    const booking = index.byDeskDate[dayKey(desk.id, date)];

    return { state: 'blockable', label: booking ? `Block (${booking.userName})` : 'Block', disabled: false, onClick: () => handleBlock(date) };
  };

  const renderCell = (date: string): WeekGridCell => {
    if (blockMode) return renderBlockModeCell(date);

    const state = getDayState(desk.id, date, index, targetUserId, range, holidays);
    const booking = index.byDeskDate[dayKey(desk.id, date)];

    switch (state) {
      case 'free':
        return { state, label: 'Free', disabled: false, onClick: () => handleFree(date) };
      case 'mine':
        return {
          state,
          label: actingForSelf ? 'You' : targetUserName,
          disabled: false,
          onClick: () => booking && setDialog(
            cancelBookingDialog(actingForSelf ? 'your' : <><strong>{targetUserName}</strong>'s</>, desk.label, date, () => onCancel(booking)),
          ),
        };
      case 'taken':
        return {
          state,
          label: booking?.userName || 'Taken',
          disabled: !canManageOthers,
          onClick: () => booking && setDialog(
            cancelBookingDialog(<><strong>{booking.userName}</strong>'s</>, desk.label, date, () => onCancel(booking)),
          ),
        };
      case 'blocked':
        return { state, label: index.blocksByDeskDate[dayKey(desk.id, date)]?.reason || 'Blocked', disabled: true };
      case 'holiday':
        return { state, label: 'Holiday', disabled: true };
      default:
        return { state, label: '', disabled: true };
    }
  };

  return (
    <div className="desk-calendar">
      <div className="desk-calendar__header">
        <div>
          <h3 className="mb-0">Desk {desk.label}</h3>
          <small className="text-muted">{desk.room}</small>
        </div>
        <button type="button" className="close" aria-label="Close" onClick={onClose}>
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      {canManageOthers && (
        <div className="custom-control custom-switch mb-2">
          <input
            type="checkbox"
            className="custom-control-input"
            id="desk-active"
            checked={desk.active}
            disabled={busy}
            onChange={handleToggleActive}
          />
          <label className="custom-control-label" htmlFor="desk-active">Desk can be booked</label>
        </div>
      )}

      {desk.active ? (
        <>
          {canManageOthers && (
            <div className="custom-control custom-switch mb-2">
              <input
                type="checkbox"
                className="custom-control-input"
                id="desk-block-mode"
                checked={blockMode}
                onChange={(event) => setBlockMode(event.target.checked)}
              />
              <label className="custom-control-label" htmlFor="desk-block-mode">Block days (workshops, events)</label>
            </div>
          )}

          <WeekGrid weeks={buildWeeks(range)} today={today} busy={busy} renderCell={renderCell} />

          <DeskLegend
            items={[
              { swatch: 'free', label: 'Free' },
              { swatch: 'mine', label: actingForSelf ? 'Yours' : targetUserName },
              { swatch: 'taken', label: 'Taken' },
              { swatch: 'blocked', label: 'Blocked' },
              { swatch: 'holiday', label: 'Holiday' },
              { swatch: 'today', label: 'Today' },
            ]}
          />
          <small className="text-muted d-block mt-2">
            {blockMode
              ? 'Click a day to block this desk (any booking that day is cancelled). Click a blocked day to unblock it.'
              : `Click a free day to book, click ${actingForSelf ? 'your' : 'their'} booking to cancel.`}
          </small>
        </>
      ) : (
        <p className="text-muted mb-0">Nobody can book this desk until it is made bookable again.</p>
      )}

      {dialog && <ConfirmDialog {...dialog} onClose={() => setDialog(null)} />}
    </div>
  );
};

export default DeskCalendar;
